// Fix: Added import for React to resolve "Cannot find namespace 'React'" error.
import React from 'react';

export enum PageType {
  SingleChoice,
  MultiChoice,
  Image,
  Audio,
}

export enum ElementType {
  Title,
  Text,
  Alert,
  Timer,
  Options,
  List,
  Image,
  Audio,
  Video,
  Button,
  Spacer,
  Loading,
  Testimonial,
  QA,
  Carousel,
  Price,
  Level,
  Seal,
  Chart,
  Report,
  BeforeAfter,
  Form,
  FbComments,
  Roulette,
  SlotMachine,
}

export interface BaseElement {
  id: string;
  type: ElementType;
  reportId?: string;
  showAfterSeconds?: number;
}

export interface TitleElement extends BaseElement {
  type: ElementType.Title;
  content: string;
  align?: 'left' | 'center' | 'right';
  fontSize?: string;
  lineHeight?: string;
}

export interface TextElement extends BaseElement {
  type: ElementType.Text;
  content: string;
  align?: 'left' | 'center' | 'right';
  size?: 'small' | 'normal' | 'large' | 'huge';
  fontSize?: string;
}

export interface AlertElement extends BaseElement {
  type: ElementType.Alert;
  content: string;
  variant?: 'info' | 'warning' | 'success' | 'error' | 'neutral';
  padding?: 'small' | 'default' | 'large';
  delaySeconds?: number;
  solid?: boolean;
  borderStyle?: 'solid' | 'dashed';
  align?: 'left' | 'center' | 'right';
}

export interface TimerElement extends BaseElement {
  type: ElementType.Timer;
  content: string;
  durationSeconds?: number;
  variant?: 'info' | 'warning' | 'success' | 'error' | 'neutral';
  padding?: 'small' | 'default' | 'large';
  solid?: boolean;
  align?: 'left' | 'center' | 'right';
}

export interface OptionsElement extends BaseElement {
  type: ElementType.Options;
  title?: string;
  description?: string;
  options: Option[];
  multiple?: boolean;
  style?: 'simple' | 'gray' | 'highlight' | 'relief';
  layout?: 'list' | 'grid2' | 'grid3' | 'grid4' | 'wrap';
  animation?: 'none' | 'border' | 'zoom';
  imageRatio?: '1:1' | '16:9' | '4:3' | '9:16';
  imageOrientation?: 'horizontal' | 'vertical';
  verticalImageRatio?: 'auto' | '1:1' | '16:9' | '4:3' | '9:16';
  mediaSize?: 'small' | 'medium' | 'large' | 'extra';
  arrangement?: 'image-text' | 'text-image';
  checkStyle?: 'none' | 'traditional' | 'square' | 'number' | 'letter' | 'arrow';
  checkPosition?: 'before' | 'after';
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
  spacing?: 'none' | 'simple' | 'medium' | 'large' | 'extra';
  textAlign?: 'left' | 'center' | 'right';
  fontWeight?: 'normal' | 'medium' | 'black';
  textColor?: string;
}

export interface ListItem {
  title: string;
  description: string;
  bgColor?: string;
  textColor?: string;
}

export interface ListElement extends BaseElement {
  type: ElementType.List;
  items: ListItem[];
  titlePosition?: 'top' | 'bottom';
  layout?: 'list' | 'grid2' | 'grid3' | 'grid4';
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
  spacing?: 'none' | 'simple' | 'medium' | 'large' | 'extra';
  textAlign?: 'left' | 'center' | 'right';
}

export interface PriceItem {
  title: string;
  prefix?: string;
  value?: string;
  suffix?: string;
  topHighlight?: string;
  bottomHighlight?: string;
  color?: 'info' | 'warning' | 'success' | 'error' | 'neutral' | 'transparent' | 'gray';
  priceType?: 'illustrative' | 'redirect';
  url?: string;
  target?: '_self' | '_blank';
  enableCheckbox?: boolean;
}

export interface PriceElement extends BaseElement {
  type: ElementType.Price;
  items: PriceItem[];
  layout?: 'horizontal' | 'vertical';
  verticalAlign?: 'top' | 'center' | 'bottom';
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
}

export interface ImageElement extends BaseElement {
  type: ElementType.Image;
  url: string;
  alt?: string;
  align?: 'left' | 'center' | 'right';
  widthPercent?: number;
  clickToNext?: boolean;
}

export interface BeforeAfterElement extends BaseElement {
  type: ElementType.BeforeAfter;
  beforeUrl: string;
  afterUrl: string;
  beforeLabel?: string;
  afterLabel?: string;
  startPercent?: number;
  orientation?: 'vertical' | 'horizontal';
  align?: 'left' | 'center' | 'right';
  widthPercent?: number;
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
}

export interface CarouselItem {
  id?: string;
  imageUrl?: string;
  alt?: string;
  description?: string;
}

export interface CarouselElement extends BaseElement {
  type: ElementType.Carousel;
  items: CarouselItem[];
  layout?: 'image' | 'image-text';
  pagination?: boolean;
  autoplay?: boolean;
  autoplayDelayMs?: number;
  align?: 'left' | 'center' | 'right';
  widthPercent?: number;
  ratio?: 'auto' | '1:1' | '16:9' | '4:3' | '9:16';
}

export interface FormField {
  id?: string;
  title: string;
  type: 'text' | 'email' | 'number' | 'date' | 'phone' | 'custom';
  placeholder?: string;
  mask?: string;
  required?: boolean;
}

export interface FormElement extends BaseElement {
  type: ElementType.Form;
  fieldId?: string;
  title: string;
  fieldType: 'text' | 'email' | 'number' | 'date' | 'phone' | 'custom';
  placeholder?: string;
  mask?: string;
  required?: boolean;
  fontSize?: 'normal' | 'large' | 'extra';
  style?: 'boxed' | 'flat';
  labelPosition?: 'tradicional' | 'none' | 'floating' | 'internal';
  align?: 'left' | 'center' | 'right';
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
}

export interface AudioElement extends BaseElement {
  type: ElementType.Audio;
  url: string;
  question?: string;
  style?: 'default' | 'whatsapp';
  avatarUrl?: string;
  showDuration?: boolean;
}

export interface VideoElement extends BaseElement {
  type: ElementType.Video;
  url: string;
  title?: string;
  allow?: string;
  referrerPolicy?: string;
  allowFullScreen?: boolean;
  autoplay?: boolean;
  cleanPlayer?: boolean;
  hideInfo?: boolean;
  aspectRatio?: '16:9' | '9:16' | '4:3' | '1:1';
}

export interface ButtonElement extends BaseElement {
  type: ElementType.Button;
  text: string;
  widthPercent?: number;
  height?: 'small' | 'medium' | 'large' | 'extra';
  align?: 'left' | 'center' | 'right';
  color?: string;
  styleVariant?: 'normal' | 'alto' | 'sombra';
  animation?: 'none' | 'zoom' | 'pulse' | 'bounce' | 'radar';
  action?: 'next' | 'redirect';
  redirectUrl?: string;
  target?: '_self' | '_blank';
}

export interface SpacerElement extends BaseElement {
  type: ElementType.Spacer;
  height?: number;
}

export interface LoadingElement extends BaseElement {
  type: ElementType.Loading;
  title?: string;
  subtitle?: string;
  subtitles?: string[]; // Array of subtitles to cycle through (max 5)
  durationSeconds?: number;
  autoProceedAfterSeconds?: number;
  showPercent?: boolean;
  barColor?: string;
}

export interface SealElement extends BaseElement {
  type: ElementType.Seal;
  icon?: string;
  title?: string;
  description?: string;
  align?: 'left' | 'center' | 'right';
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
  iconColor?: string;
  iconSize?: 'small' | 'medium' | 'large';
  backgroundColor?: string;
  titleColor?: string;
  textColor?: string;
}

export interface ChartItem {
  type: 'bar' | 'donut';
  title?: string;
  text?: string;
  percent?: number;
  color?: string;
  percentColor?: string;
}

export interface ChartElement extends BaseElement {
  type: ElementType.Chart;
  items: ChartItem[];
  layout?: 'list' | 'grid2' | 'grid3';
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
  animation?: 'none' | 'border' | 'zoom';
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
}

export interface ReportElement extends BaseElement {
  type: ElementType.Report;
  title?: string;
  resultLabel?: string;
  subtitle?: string;
  score?: number;
  imageUrl?: string;
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
  backgroundColor?: string;
  borderColor?: string;
  titleColor?: string;
  textColor?: string;
  scoreColor?: string;
  percentColor?: string;
}

export interface FbCommentItem {
  id: string;
  avatarUrl?: string;
  name: string;
  timeAgo: string;
  content: string;
  imageUrl?: string; // Caso queira postar uma imagem no comentário
}

export interface FbCommentsElement extends BaseElement {
  type: ElementType.FbComments;
  comments: FbCommentItem[];
  totalComments?: number; // Para exibir "X of Y comments"
  showActions?: boolean; // Exibir botões Curtir/Responder/Compartilhar (padrão: false)
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
}

export interface RouletteItem {
  id: string;
  emoji: string;
  label: string;
}

export interface RouletteElement extends BaseElement {
  type: ElementType.Roulette;
  title: string;
  buttonText: string;
  winningMessage: string;
  subMessage?: string;
  items: RouletteItem[];
  maxItems?: number;
  align?: 'left' | 'center' | 'right';
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
  colorScheme?: 'colorful' | 'grayscale';
}

export interface SlotMachineItem {
  id: string;
  emoji: string;
}

export interface SlotMachineElement extends BaseElement {
  type: ElementType.SlotMachine;
  title: string;
  winningMessage: string;
  losingMessage?: string;
  subMessage?: string;
  maxAttempts?: number;
  alwaysWin?: boolean;
  align?: 'left' | 'center' | 'right';
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
}

export type PageElement = TitleElement | TextElement | AlertElement | TimerElement | OptionsElement | ListElement | PriceElement | ImageElement | BeforeAfterElement | AudioElement | VideoElement | ButtonElement | SpacerElement | LoadingElement | TestimonialElement | QAElement | CarouselElement | LevelElement | SealElement | ChartElement | ReportElement | FormElement | FbCommentsElement | RouletteElement | SlotMachineElement;

export interface Option {
  id: string;
  text: string;
  icon?: React.ComponentType<{ className?: string }>;
  imageUrl?: string;
  emoji?: string;
  mediaType?: 'none' | 'emoji' | 'image';
}

export interface TestimonialElement extends BaseElement {
  type: ElementType.Testimonial;
  name: string;
  info?: string;
  stars?: number;
  text: string;
  imageUrl?: string;
  starColor?: string;
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
}

export interface QAItem {
  question: string;
  answer: string;
}

export interface QAElement extends BaseElement {
  type: ElementType.QA;
  items: QAItem[];
  openFirst?: boolean;
  allowMultiple?: boolean;
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
}

export interface LevelElement extends BaseElement {
  type: ElementType.Level;
  title?: string;
  subtitle?: string;
  percent?: number;
  indicatorText?: string;
  legendLeft?: string;
  legendCenter?: string;
  legendRight?: string;
  showMeter?: boolean;
  showProgress?: boolean;
  styleType?: 'dashes' | 'line';
  colorTheme?: 'theme' | 'greenToRed' | 'redToGreen' | 'primary' | 'red' | 'blue' | 'green' | 'yellow';
  border?: 'none' | 'small' | 'medium' | 'large';
  shadow?: 'none' | 'small' | 'medium' | 'large';
  backgroundColor?: string;
  titleColor?: string;
  textColor?: string;
  barColor?: string;
}

export interface Page {
  id: number;
  uniqueId: string;  // UUID único e imutável para tracking de analytics
  title: string;
  elements: PageElement[];  // Elementos renderizados na página

  // Configurações opcionais
  confettiConfig?: {
    enabled: boolean;
    style: 'paper' | 'circles' | 'stars' | 'fireworks';
    amount: 'low' | 'medium' | 'high';
    direction: 'center' | 'top' | 'sides';
  };
  confettiOnEntryConfig?: {
    enabled: boolean;
    style: 'paper' | 'circles' | 'stars' | 'fireworks';
    amount: 'low' | 'medium' | 'high';
    direction: 'center' | 'top' | 'sides';
  };
  scoreConfig?: {
    enabled: boolean;
    points: number;
  };
  scorePopupConfig?: {
    enabled: boolean;
    text?: string;
    footer?: string;
  };
  hideBackButton?: boolean;
}

export interface QuizConfig {
  colors: {
    background: string;
    primary: string;
    title?: string;
    text?: string;
  };
  fontSizes: {
    title: string;
    subtitle: string;
    option: string;
  };
  fontFamily: string;
  contentAlignment?: 'center' | 'top';
  contentWidth?: 'small' | 'medium' | 'large';
  elementSize?: 'small' | 'medium' | 'large' | 'extra';
  spacing?: 'none' | 'simple' | 'medium' | 'large' | 'extra';
  borderRadius?: 'none' | 'light' | 'medium' | 'heavy';
  containerStyle?: 'flat' | 'boxed';
  header?: {
    logoUrl?: string;
    logoSize?: 'small' | 'large';
    logoPosition?: 'center' | 'left';
    showBackButton?: boolean;
    showStepIndicator?: boolean;
    progressBarStyle?: 'default' | 'flat' | 'hidden';
    hideOnFirstPage?: boolean;
  };
  gamification?: {
    confettiEnabled?: boolean;
    coinsEnabled?: boolean;
    coinsPreText?: string;
    coinsPostText?: string;
    style?: 'coins' | 'money';
    sound?: 'default' | 'cash-register' | 'bonus-points' | 'collect-points' | 'plin-points';
    animationType?: 'simple' | 'flying';
    currencySymbol?: string;
  };
  validation?: {
    requiredMessage?: string;
  };
  scripts?: {
    head?: string;
    footer?: string;
  };
  seoTitle?: string;
  seoDescription?: string;
  session?: {
    mode: 'reset' | 'recover';
    durationDays: number;
  };
}

export interface QuizData {
  name?: string;
  config: QuizConfig;
  pages: Page[];
}

export interface StoredQuiz {
  id: string;
  name: string;
  slug?: string;
  data: QuizData;
  status: 'draft' | 'published';
  share_id?: string;
  createdAt: number;
  updatedAt: number;
}

export interface ProjectMeta {
  id: string;
  name: string;
  quizzes: StoredQuiz[];
}
